import React, { useEffect } from "react";
import { observer, useLocalObservable } from "mobx-react-lite";
import { HeroDataInfo, ScreenResponsive } from "../../../components/organisms";
import { Box, ButtonLayout, Grid, GridItem, IconLocationMapLight, skinVars, useScreenSize } from "@telefonica/mistica";
import { strings } from "../../../localization";
import { Button } from "../../../components/atoms";
import { AddressInputGroup } from "../../../components/organisms/AddressInputGroup";
import { useGlobalStore } from "../../../contexts/globalStoreContext";
import { formatCEP, onlyNumber } from "../../../utils/formatters";
import { Store } from "./store";
import { useAnalytics, useNavigateCustom } from "../../../hooks";

const AddressInput: React.FC = observer(() => {
	const navigate = useNavigateCustom();
	const { authenticationStore, fintechRegisterStore } = useGlobalStore();
	const { isMobile, isDesktopOrBigger } = useScreenSize();
	const store = useLocalObservable(() => new Store(authenticationStore, fintechRegisterStore));
	const { anticipation } = strings.pages.navigation;
	const { title, description } = strings.pages.addressInput;
	const { buttonPrimary } = strings.components.buttons;
	const { updatePageView, addAnalyticEvent } = useAnalytics();
	const [isInvalidCep, setIsInvalidCep] = React.useState(false);

	const [isLoadingInitialData, setIsLoadingInitialData] = React.useState<boolean>(true);

	const handleNonInteractionEvent = (): void => {
		if (store.hasPostalCodeError.value && !isInvalidCep) {
			const { postalCodeErrorText } = strings.components.addressInputGroup;

			addAnalyticEvent({
				component: "body",
				action: "view-error",
				step: "fgts:cadastro:dados-endereco",
				event: "non_interaction",
				description: postalCodeErrorText,
			}, { applySanitize: true });

			return setIsInvalidCep(true);
		} else if (!store.hasPostalCodeError.value) {
			setIsInvalidCep(false);
		}
	};

	const onButtonAction = async (): Promise<void> => {
		addAnalyticEvent({
			component: "button",
			action: "click.button",
			step: "fgts:cadastro:dados-endereco",
			event: "interaction",
			description: buttonPrimary,
		}, { applySanitize: true });

		await store.fetchData(() => navigate("/fintech-registration/personal-aditional-info"));
	};

	useEffect(() => {
		updatePageView({
			name: "Vivo Money Cadastro FGTS Endereco",
			pagePath: "/cadastro-fgts/endereco",
		});
	}, []);

	const handleFullRegistration = async (): Promise<void> => {
		try {
			setIsLoadingInitialData(true);
			await fintechRegisterStore.getPersonalFetch.fetch();
		} catch (error) {
			console.error(error);
		} finally {
			setIsLoadingInitialData(false);
		}
	};

	useEffect(() => {
		handleFullRegistration();
	}, []);

	useEffect(() => {
		if (!isLoadingInitialData && !fintechRegisterStore.getHas4p && fintechRegisterStore.postalCode.field.value.length === 9) {
			store.getAddressByPostalCode.fetch();
		}

		handleNonInteractionEvent();
	}, [fintechRegisterStore.postalCode.field.value]);

	return (
		<ScreenResponsive navigationTitle={anticipation} onBack={() => navigate(-1)}>
			<Grid columns={isDesktopOrBigger ? 4 : 1} justifyContent="center">
				<GridItem columnStart={2} span={isDesktopOrBigger ? 2 : 1}>
					<Box width={isDesktopOrBigger ? 600 : "100%"} paddingY={isMobile ? 32 : 40}>
						<HeroDataInfo
							icon={<IconLocationMapLight color={skinVars.colors.brand} size={64} />}
							title={title}
							description={description}
						/>
						<Grid columns={{ maxSize: 392, minSize: 327 }} justifyContent="center">
							<AddressInputGroup
								additionalInfo={fintechRegisterStore.additionalInfo.field.value}
								address={fintechRegisterStore.address.field.value}
								districtName={fintechRegisterStore.districtName.field.value}
								countrySubDivision={fintechRegisterStore.countrySubDivision.field.value}
								number={fintechRegisterStore.number.field.value}
								postalCode={fintechRegisterStore.postalCode.field.value}
								townName={fintechRegisterStore.townName.field.value}
								onChangeAdditionalInfo={(t) => fintechRegisterStore.additionalInfo.onChange(t)}
								onChangeAddress={(t) => {
									// Limpar mensagem de erro quando o usuário digita
									if (store.addressFieldError.value) {
										store.addressFieldError.set(false);
									}
									fintechRegisterStore.address.onChange(t);
								}}
								onChangeDistrictName={(t) => fintechRegisterStore.districtName.onChange(t)}
								onChangeNumber={(t) => fintechRegisterStore.number.onChange(t)}
								onChangeCountrySubDivision={(t) => fintechRegisterStore.countrySubDivision.onChange(t)}
								onChangePostalCode={(t) => fintechRegisterStore.postalCode.onChange(formatCEP(onlyNumber(t)))}
								onChangeTownName={(t) => fintechRegisterStore.townName.onChange(t)}
								isPostalCodeError={store.hasPostalCodeError.value}
								isAddressError={store.addressFieldError.value}
								addressErrorText={store.addressFieldErrorMessage.value}
								isAddressDisabled={store.isLoading}
								isAdditionalInfoDisabled={store.isLoading}
								isCountrySubDivisionDisabled={store.isLoading}
								isDistrictNameDisabled={store.isLoading}
								isNumberDisabled={store.isLoading}
								isPostalCodeDisabled={store.isLoading}
								isTownNameDisabled={store.isLoading}
							/>
							<ButtonLayout>
								<Button
									isLoading={store.isLoading}
									textButton={buttonPrimary}
									onAction={() => {
										// Sanitizar e validar antes de prosseguir
										store.sanitizeAddress();
										if (store.validateAddress()) {
											onButtonAction();
										}
									}}
									isDisabled={store.isSubmitButtonDisabled}
								/>
							</ButtonLayout>
						</Grid>
					</Box>
				</GridItem>
			</Grid>
		</ScreenResponsive>
	);
});

export default AddressInput;
